import { useId } from 'react';

/**
 * The FilmSloth mascot, in the two sizes the brand actually needs.
 *
 * Both are inline SVG rather than an <img>: they inherit nothing from the page
 * but cost no extra request, stay sharp at any size, and — the reason that
 * decided it — the app shell is precached by the service worker, so an image
 * file here would be one more thing to keep in that list.
 *
 * The gradient ids are per-instance (`useId`). Two of these on one page with
 * hard-coded ids would collide, and the second one would silently pick up the
 * first one's gradients.
 *
 * The drawing is meant to read as *cute* first, and cute is mostly proportion,
 * not detail. Three things carry it, and edits tend to break them:
 *
 * - The eyes are large and sit low on the face. That is the whole baby schema;
 *   shrink them or raise them and the mascot turns severe.
 * - The brown patch only *frames* a much smaller eye — it is the sloth's
 *   stripe. A narrow slanted patch with no eye inside reads as an angry brow.
 * - The fur outline is a circle with many small lobes, and the tufts on top are
 *   uneven. Even tufts read as a hair bow rather than a cowlick.
 */

interface SlothProps {
  className?: string;
  /** Rendered size in px. Both viewBoxes are square, so one number is enough. */
  size?: number;
}

/** Shared palette. Warm browns throughout — the mascot carries no interface colour. */
const FUR_LIGHT = '#dcc09a';
const FUR_DARK = '#a8825a';
const FACE = '#fbf0dd';
const PATCH = '#8b6647';
const INK = '#3b2a1c';
const CHEEK = '#e79a86';

function Gradients({ plate, fur }: { plate: string; fur: string }) {
  return (
    <defs>
      <linearGradient id={plate} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#221c14" />
        <stop offset="1" stopColor="#100d09" />
      </linearGradient>
      <linearGradient id={fur} x1="0.2" y1="0" x2="0.5" y2="1">
        <stop offset="0" stopColor={FUR_LIGHT} />
        <stop offset="1" stopColor={FUR_DARK} />
      </linearGradient>
    </defs>
  );
}

/** The head, framed. This is the app icon, the favicon and the wordmark chip. */
export function SlothMark({ className, size = 120 }: SlothProps) {
  const uid = useId();
  const plate = `${uid}-p`;
  const fur = `${uid}-f`;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="FilmSloth"
    >
      <Gradients plate={plate} fur={fur} />
      <rect width="120" height="120" rx="27" fill={`url(#${plate})`} />
      {/* Cowlick, behind the head so the tufts grow out of the fur. */}
      <g fill={FUR_DARK}>
        <path d="M46.4 30Q49 17 51.6 30Z" transform="rotate(-13 49 30)" />
        <path d="M52.9 30Q55.5 12 58.1 30Z" transform="rotate(-5 55.5 30)" />
        <path d="M60.4 30Q63 14 65.6 30Z" transform="rotate(4 63 30)" />
        <path d="M67.9 30Q70.5 18 73.1 30Z" transform="rotate(12 70.5 30)" />
      </g>
      <path
        d="M96 62Q98.45 69.65 93.26 75.78Q92.59 83.78 85.46 87.46Q81.78 94.59 73.78 95.26Q67.65 100.45 60 98Q52.35 100.45 46.22 95.26Q38.22 94.59 34.54 87.46Q27.41 83.78 26.74 75.78Q21.55 69.65 24 62Q21.55 54.35 26.74 48.22Q27.41 40.22 34.54 36.54Q38.22 29.41 46.22 28.74Q52.35 23.55 60 26Q67.65 23.55 73.78 28.74Q81.78 29.41 85.46 36.54Q92.59 40.22 93.26 48.22Q98.45 54.35 96 62Z"
        fill={`url(#${fur})`}
      />
      <ellipse cx="60" cy="64" rx="28" ry="25" fill={FACE} />
      <g fill={CHEEK} opacity="0.42">
        <ellipse cx="38" cy="72" rx="6.5" ry="4.6" />
        <ellipse cx="82" cy="72" rx="6.5" ry="4.6" />
      </g>
      <ellipse cx="46" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(-13 46 59)" />
      <circle cx="46" cy="59.5" r="6.4" fill={INK} />
      <circle cx="43.8" cy="56.8" r="2.4" fill="#fff" />
      <circle cx="48" cy="61.6" r="1.2" fill="#fff" opacity="0.8" />
      <ellipse cx="74" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(13 74 59)" />
      <circle cx="74" cy="59.5" r="6.4" fill={INK} />
      <circle cx="76.2" cy="56.8" r="2.4" fill="#fff" />
      <circle cx="72" cy="61.6" r="1.2" fill="#fff" opacity="0.8" />
      <path d="M54 73Q60 68.5 66 73Q60 79 54 73Z" fill={INK} />
      <path d="M50 80Q60 88 70 80" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** A small raised paw, reused across the vote poses below. */
function Paw({
  fur,
  x,
  y,
  rotate,
  rx = 11,
  ry = 8.5,
}: {
  fur: string;
  x: number;
  y: number;
  rotate: number;
  rx?: number;
  ry?: number;
}) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate})`}>
      <ellipse cx="0" cy="0" rx={rx} ry={ry} fill={`url(#${fur})`} />
      <circle cx={-rx * 0.55} cy={-ry * 0.7} r="2.6" fill={FUR_DARK} />
      <circle cx="0" cy={-ry * 1} r="2.6" fill={FUR_DARK} />
      <circle cx={rx * 0.55} cy={-ry * 0.7} r="2.6" fill={FUR_DARK} />
    </g>
  );
}

/** Fur tufts + fur blob + face plate, shared by the three vote poses. */
function VoteHead({ fur, cheekOpacity = 0.42 }: { fur: string; cheekOpacity?: number }) {
  return (
    <>
      <g fill={FUR_DARK}>
        <path d="M46.4 30Q49 17 51.6 30Z" transform="rotate(-13 49 30)" />
        <path d="M52.9 30Q55.5 12 58.1 30Z" transform="rotate(-5 55.5 30)" />
        <path d="M60.4 30Q63 14 65.6 30Z" transform="rotate(4 63 30)" />
        <path d="M67.9 30Q70.5 18 73.1 30Z" transform="rotate(12 70.5 30)" />
      </g>
      <path
        d="M96 62Q98.45 69.65 93.26 75.78Q92.59 83.78 85.46 87.46Q81.78 94.59 73.78 95.26Q67.65 100.45 60 98Q52.35 100.45 46.22 95.26Q38.22 94.59 34.54 87.46Q27.41 83.78 26.74 75.78Q21.55 69.65 24 62Q21.55 54.35 26.74 48.22Q27.41 40.22 34.54 36.54Q38.22 29.41 46.22 28.74Q52.35 23.55 60 26Q67.65 23.55 73.78 28.74Q81.78 29.41 85.46 36.54Q92.59 40.22 93.26 48.22Q98.45 54.35 96 62Z"
        fill={`url(#${fur})`}
      />
      <ellipse cx="60" cy="64" rx="28" ry="25" fill={FACE} />
      <g fill={CHEEK} opacity={cheekOpacity}>
        <ellipse cx="38" cy="72" rx="6.5" ry="4.6" />
        <ellipse cx="82" cy="72" rx="6.5" ry="4.6" />
      </g>
    </>
  );
}

/**
 * The three vote-button icons: the same head as `SlothMark`, only the eyes,
 * mouth and a raised paw change per pose. Kept as one animal reacting three
 * ways rather than three different icons — see the module doc above for why
 * that consistency matters at this size.
 */

/** Eyes shut, mouth turned down, one paw thrown up in a "no thanks" gesture. */
export function SlothVoteNo({ className, size = 28 }: SlothProps) {
  const uid = useId();
  const fur = `${uid}-f`;
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <defs>
        <linearGradient id={fur} x1="0.2" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor={FUR_LIGHT} />
          <stop offset="1" stopColor={FUR_DARK} />
        </linearGradient>
      </defs>
      <VoteHead fur={fur} />
      <ellipse cx="46" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(-13 46 59)" />
      <ellipse cx="74" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(13 74 59)" />
      <path d="M40 60Q46 56 52 60" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M68 60Q74 56 80 60" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M50 82Q60 76 70 82" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      <Paw fur={fur} x={34} y={44} rotate={-24} />
    </svg>
  );
}

/** Both paws up in a shrug, flat mouth, calm round eyes. */
export function SlothVoteNeutral({ className, size = 28 }: SlothProps) {
  const uid = useId();
  const fur = `${uid}-f`;
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <defs>
        <linearGradient id={fur} x1="0.2" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor={FUR_LIGHT} />
          <stop offset="1" stopColor={FUR_DARK} />
        </linearGradient>
      </defs>
      <VoteHead fur={fur} />
      <ellipse cx="46" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(-13 46 59)" />
      <circle cx="46" cy="59.5" r="6.4" fill={INK} />
      <circle cx="43.8" cy="56.8" r="2.4" fill="#fff" />
      <ellipse cx="74" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(13 74 59)" />
      <circle cx="74" cy="59.5" r="6.4" fill={INK} />
      <circle cx="76.2" cy="56.8" r="2.4" fill="#fff" />
      <path d="M50 80L70 80" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      <Paw fur={fur} x={26} y={50} rotate={-30} rx={10} ry={8} />
      <Paw fur={fur} x={94} y={50} rotate={30} rx={10} ry={8} />
    </svg>
  );
}

/** Paws thrown up in a cheer, happy crescent eyes, wide open grin. */
export function SlothVoteYes({ className, size = 28 }: SlothProps) {
  const uid = useId();
  const fur = `${uid}-f`;
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <defs>
        <linearGradient id={fur} x1="0.2" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor={FUR_LIGHT} />
          <stop offset="1" stopColor={FUR_DARK} />
        </linearGradient>
      </defs>
      <VoteHead fur={fur} cheekOpacity={0.6} />
      <ellipse cx="46" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(-13 46 59)" />
      <ellipse cx="74" cy="59" rx="13" ry="10.5" fill={PATCH} transform="rotate(13 74 59)" />
      <path d="M40 60Q46 54 52 60" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M68 60Q74 54 80 60" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path
        d="M48 78Q60 94 72 78"
        fill="none"
        stroke={INK}
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <Paw fur={fur} x={32} y={28} rotate={-35} rx={10} ry={8} />
      <Paw fur={fur} x={88} y={28} rotate={35} rx={10} ry={8} />
    </svg>
  );
}

/**
 * The whole animal, sitting with its popcorn — the home screen's one
 * illustration. Deliberately drawn without a background plate so it sits on
 * the page rather than in a box; the hero is artwork, not an icon blown up.
 *
 * The tub is the brand's only film reference, and it lives here rather than in
 * the mark: at icon sizes it collapses into a smudge, but at hero size it says
 * "cinema" without a caption. It sits low on purpose — drawn any higher, the
 * head covers the popcorn and all that is left is a striped box.
 */
export function SlothHero({ className, size = 220 }: SlothProps) {
  const uid = useId();
  const plate = `${uid}-p`;
  const fur = `${uid}-f`;
  const tub = `${uid}-t`;
  const tubShape = 'M45 96H75L71.5 117Q60 120 48.5 117Z';
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="4 8 112 112"
      xmlns="http://www.w3.org/2000/svg"
      // Decorative: the wordmark right below already names the app, so a
      // second announcement would just make screen readers repeat themselves.
      aria-hidden="true"
      focusable="false"
    >
      <Gradients plate={plate} fur={fur} />
      <defs>
        <clipPath id={tub}>
          <path d={tubShape} />
        </clipPath>
      </defs>
      <path
        d="M84 84Q85.74 89.29 81.62 93.37Q80.64 98.81 74.96 100.89Q71.45 105.41 65.34 105.06Q60 107.76 54.66 105.06Q48.55 105.41 45.04 100.89Q39.36 98.81 38.38 93.37Q34.26 89.29 36 84Q34.26 78.71 38.38 74.63Q39.36 69.19 45.04 67.11Q48.55 62.59 54.66 62.94Q60 60.24 65.34 62.94Q71.45 62.59 74.96 67.11Q80.64 69.19 81.62 74.63Q85.74 78.71 84 84Z"
        fill={`url(#${fur})`}
      />
      {/* Popcorn, then the arms over it so the paws hold the tub. */}
      <g fill="#ffe6ae">
        <circle cx="48" cy="93" r="5.4" />
        <circle cx="58" cy="90" r="6.2" />
        <circle cx="69" cy="92.5" r="5.6" />
        <circle cx="63" cy="95" r="4.6" />
      </g>
      <path d={tubShape} fill="#fdf6ec" />
      <g clipPath={`url(#${tub})`} stroke="#d4544e" strokeWidth="4.8">
        <path d="M52 96L54 118" />
        <path d="M60 96L60 118" />
        <path d="M68 96L66 118" />
      </g>
      <g stroke={`url(#${fur})`} strokeWidth="10" strokeLinecap="round" fill="none">
        <path d="M40 80C35 89 39 98 46 102" />
        <path d="M80 80C85 89 81 98 74 102" />
      </g>
      <g stroke={FACE} strokeWidth="2.5" fill="none" strokeLinecap="round">
        <path d="M46 100q1 3.4-1.4 5.6" />
        <path d="M49.6 102.4q1 3.4-1.4 5.6" />
        <path d="M74 100q-1 3.4 1.4 5.6" />
        <path d="M70.4 102.4q-1 3.4 1.4 5.6" />
      </g>
      <g fill={FUR_DARK}>
        <path d="M47.49 27.52Q49.88 15.56 52.27 27.52Z" transform="rotate(-13 49.88 27.52)" />
        <path d="M53.47 27.52Q55.86 10.96 58.25 27.52Z" transform="rotate(-5 55.86 27.52)" />
        <path d="M60.37 27.52Q62.76 12.8 65.15 27.52Z" transform="rotate(4 62.76 27.52)" />
        <path d="M67.27 27.52Q69.66 16.48 72.05 27.52Z" transform="rotate(12 69.66 27.52)" />
      </g>
      <path
        d="M90 56Q92.18 62.84 87.41 68.2Q86.62 75.34 80.07 78.29Q76.45 84.49 69.27 84.53Q63.44 88.72 56.86 85.84Q49.83 87.29 45 81.98Q37.99 80.45 35.73 73.63Q29.94 69.38 30.66 62.24Q27.1 56 30.66 49.76Q29.94 42.62 35.73 38.37Q37.99 31.55 45 30.02Q49.83 24.71 56.86 26.16Q63.44 23.28 69.27 27.47Q76.45 27.51 80.07 33.71Q86.62 36.66 87.41 43.8Q92.18 49.16 90 56Z"
        fill={`url(#${fur})`}
      />
      <ellipse cx="60" cy="58" rx="23" ry="21" fill={FACE} />
      <g fill={CHEEK} opacity="0.42">
        <ellipse cx="41.52" cy="64.72" rx="5.46" ry="3.86" />
        <ellipse cx="78.48" cy="64.72" rx="5.46" ry="3.86" />
      </g>
      <ellipse
        cx="48.24"
        cy="53.8"
        rx="10.92"
        ry="8.82"
        fill={PATCH}
        transform="rotate(-13 48.24 53.8)"
      />
      <circle cx="48.24" cy="54.22" r="5.38" fill={INK} />
      <circle cx="46.39" cy="51.95" r="2.02" fill="#fff" />
      <circle cx="49.92" cy="55.98" r="1.01" fill="#fff" opacity="0.8" />
      <ellipse
        cx="71.76"
        cy="53.8"
        rx="10.92"
        ry="8.82"
        fill={PATCH}
        transform="rotate(13 71.76 53.8)"
      />
      <circle cx="71.76" cy="54.22" r="5.38" fill={INK} />
      <circle cx="73.61" cy="51.95" r="2.02" fill="#fff" />
      <circle cx="70.08" cy="55.98" r="1.01" fill="#fff" opacity="0.8" />
      <path d="M54.96 65.56Q60 61.78 65.04 65.56Q60 70.6 54.96 65.56Z" fill={INK} />
      <path
        d="M51.6 71.44Q60 78.16 68.4 71.44"
        fill="none"
        stroke={INK}
        strokeWidth="2.52"
        strokeLinecap="round"
      />
    </svg>
  );
}
